import comm from "csi/comm";
import PostRequest from "csi/comm/PostRequest";
import ExtendedPath from "csi/core/ExtendedPath";
import ReflectionAttribute from "csi/reflection/ReflectionAttribute";
import { ATTR_CURRENT_REVISION, ATTR_LATEST_REVISION, ATTR_MASTER, ATTR_REVISIONS, getDomainClass, getNodeClass } from "csi/reflection/reflection";
import { ICache } from "csi/typings";
import { isNullOrUndefined } from "csi/utility/coreUtility";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import { compact, isEmpty } from "lodash";
import { ATTR_PARENT, C8Node, C8NodeOrCnl, C8NodeType } from "src/c8-platform/type/node";
import { VALUE_TYPE, cnl } from "src/c8-platform/type/value";

let _requestedUrls: cnl[];

/**
 * Construct attribute value for ref* attribute with given url and index
 */
function _getRefAttributeValue(ra: ReflectionAttribute, url: cnl, index?: string | number): cnl | cnl[] | Record<string, cnl> {
	const { valueType } = ra;

	if (valueType === VALUE_TYPE.REF) {
		return url;
	} else if (valueType === VALUE_TYPE.REF_MAP) {
		if (isNullOrUndefined(index)) {
			throw new Error("_getRefAttributeValue - index is required for refmap");
		}

		return { [String(index)]: url };
	}

	if (isNullOrUndefined(index)) {
		return [url];
	}

	// no support for array indices yet
	throw new Error("_getRefAttributeValue - index for reflist or refset not yet supported");
}

/**
 * Create network of nodes in the cache to satisfy the given extended path, returning the starting node for the path;
 * throws exception if the path contains a parent step as those cannot be supported here
 */
export function createClientNodesForPath(ep: ExtendedPath, cache: ICache): C8Node {
	const startNode = xml.nodeCreateClientNode(null, ep.getStartType());
	let node = startNode;

	cache.add(node);

	ep.steps?.some(({ a, c, key, isParent }, index) => {
		if (isParent) {
			throw new Error(`createClientNodesForPath - does not work with parent steps: ${ep.toString()}`);
		}

		const nextNode = xml.nodeCreateClientNode(null, c);
		const nextNodeUrl = nextNode.$URL;
		const nodeUrl = node.$URL;
		const ra = ep.getStepRelation(index);
		const value = _getRefAttributeValue(ra, nextNodeUrl, key);

		xml.nodeCreateClientAttr(node, a, ra.valueType, value);

		const rc = getNodeClass(node);
		if (rc.isMaster() && (rc.revisionType === nextNode.$Type)) {
			xml.nodeCreateClientAttr(node, ATTR_REVISIONS, VALUE_TYPE.REF_VECT, [nextNodeUrl]);
			xml.nodeCreateClientAttr(node, ATTR_CURRENT_REVISION, VALUE_TYPE.REF, nextNodeUrl);
			xml.nodeCreateClientAttr(node, ATTR_LATEST_REVISION, VALUE_TYPE.REF, nextNodeUrl);
			xml.nodeCreateClientAttr(nextNode, ATTR_MASTER, VALUE_TYPE.REF, nodeUrl);
			xml.nodeCreateClientAttr(nextNode, ATTR_PARENT, VALUE_TYPE.REF, nodeUrl);
		} else if (ra.isComposite) {
			xml.nodeCreateClientAttr(nextNode, ATTR_PARENT, VALUE_TYPE.REF, nodeUrl);
		}

		node = nextNode;
		cache.add(node);
	});

	return startNode;
}

/**
 * Return cnl/C8Node pair for given cnl or node
 */
export function getUrlNodePair(urlOrNode: C8NodeOrCnl, cache: ICache): [cnl, C8Node] {
	if (typeof urlOrNode === "string") {
		return [urlOrNode, cache.get(urlOrNode)];
	}

	return [urlOrNode?.$URL, urlOrNode];
}

/**
 * NOTE: This relies on all nodes being in the cache.
 * Return nodes that are parents of the given url, starting with node for given url and ending with furthest parent.
 */
export function getAncestorNodes(url: string, cache: ICache, includeNode: boolean = false): C8Node[] {
	const nodes: C8Node[] = [];
	let node = cache.get(url);

	while (node) {
		// skip secondary classes as the user only cares about domain classes;
		// skip singleton classes as they are there to support views rather than real user objects
		if (getDomainClass(node.$Type) && !getNodeClass(node)
			.isSingleton()) {
			if (includeNode) {
				nodes.push(node);
			} else {
				includeNode = true;
			}
		}

		node = cache.get(node.__Parent__);
	}

	return nodes;
}

/**
 * Find urls and paths that are not found resolved in given cache
 */
export function getNodeSearchUnresolvedUrlPaths(cache: ICache, urlsOrNodes: C8NodeOrCnl[], paths?: string[]): [cnl[], string[]] {
	const unresolvedUrls: Set<cnl> = new Set();
	const unresolvedPaths: Set<string> = new Set();
	let allPaths = false;

	for (const urlOrNode of urlsOrNodes) {
		const [url, node] = getUrlNodePair(urlOrNode, cache);

		if (node) {
			// check each path if it is known
			if (paths) {
				for (const path of paths) {
					if (cache.getPathNodes(url, path, null, true) === undefined) {
						unresolvedUrls.add(url);

						if (allPaths) {
							// if need to get all paths, then no need to iterate further now that url is missing at least one path
							break;
						} else {
							// track path that is missing
							unresolvedPaths.add(path);
						}
					}
				}
			}
		} else if (xml.isValidURL(url)) {
			// if url is missing, then all paths are missing too
			unresolvedUrls.add(url);
			allPaths = true;
		}
	}

	return [Array.from(unresolvedUrls), allPaths ? paths : Array.from(unresolvedPaths)];
}

/** request a new url from the server, optionally indicating how many will be needed to optimize query for them */
export async function requestUrl(totalNeeded?: number): Promise<cnl> {
	if (!_requestedUrls?.length) {
		_requestedUrls = await requestUrls(totalNeeded || 5);
	}

	return _requestedUrls.shift();
}

/** request a list of new urls from the server */
export async function requestUrls(count: number): Promise<cnl[]> {
	const request = new PostRequest("NodeProcessor", "CreateNodeURL", { Count: count });
	const response = await request.go();
	return response.getUrls();
}

/**
 * Request node from server if not in given cache and return
 */
export async function resolveNode(url: cnl, cache?: ICache): Promise<C8Node> {
	let node = cache?.get(url);

	if (!node) {
		const request = comm.getNodeRequest(url);

		// url could not be valid, so test for request
		if (request) {
			const response = await request.go();
			cache?.cacheResponse(response);
			node = response.getResultNode();
		}
	}

	return node;
}

/**
 * Request nodes from server if not in given cache and return nodes for all urls
 */
export async function resolveNodes(urls: cnl[], cache?: ICache, bCompact?: boolean): Promise<C8Node[]> {
	const validUrls = urls.filter(xml.isValidURL);
	const missingUrls = cache ? validUrls.filter((url) => !cache.get(url)) : validUrls;

	if (missingUrls.length) {
		const request = comm.getNodeRequest(missingUrls);
		const response = await request.go();

		if (cache) {
			// cache missing nodes
			cache.cacheResponse(response);
		} else {
			// if no cache, then all urls will be in response
			return response.getResultNodes();
		}
	}

	const nodes = validUrls.map((url) => cache.get(url));
	return bCompact ? compact(nodes) : nodes;
}

/**
 * Request nodes from server if not in given cache and return nodes for given urls
 */
export async function resolveNodesWithDependencies(urlsOrNodes: C8NodeOrCnl[], paths: string[], cache: ICache): Promise<C8Node[]> {
	if (isEmpty(urlsOrNodes)) {
		return;
	}
	const [unresolvedUrls, unresolvedPaths] = getNodeSearchUnresolvedUrlPaths(cache, urlsOrNodes, paths);
	const request = comm.getNodeRequest(unresolvedUrls)
		?.addDependencyPaths(unresolvedPaths);

	if (request) {
		const response = await request.go();
		cache.cacheResponse(response);
	}

	return urlsOrNodes.map((urlOrNode) => {
		const [url, node] = getUrlNodePair(urlOrNode, cache);
		return node;
	});
}

/** get the node names by the given urls */
export async function getNodeNames(urls: cnl[], nodeType: C8NodeType): Promise<Map<cnl, string>> {
	const nameMap = new Map<cnl, string>();
	const compactedUrls = compact(urls);

	if (!urls.length) {
		return nameMap;
	}

	const search = new Search({ Operation: "NamesByXML" }).byType(nodeType);
	const query = search.getQuery().Or();

	for (const url of compactedUrls) {
		query.Node(xml.PROP_URL, xml.OP_EQUALS, url);
	}

	const response = await search.execute();
	const names = response.getNameAndUrls();

	for (const { Name, $ } of names) {
		nameMap.set($, Name);
	}

	return nameMap;
}